import { createSlice } from "@reduxjs/toolkit";

/**
 * Sidebar state interface defining the shape of the sidebar state in the store.
 */
interface SidebarState {
  isOpen: boolean;
}

/**
 * Initial state for the sidebar.
 * It checks localStorage to persist sidebar open/close state across sessions.
 */
const initialState: SidebarState = {
  isOpen: localStorage.getItem("sidebarState") === "open",
};

/**
 * Redux slice for managing sidebar open/close state globally.
 * Includes toggle, open, and close actions with localStorage sync.
 */
const sidebarSlice = createSlice({
  name: "sidebar",
  initialState,
  reducers: {
    /**
     * Toggles sidebar open/close state.
     * Updates localStorage accordingly.
     */
    toggleSidebar: (state) => {
      state.isOpen = !state.isOpen;
      localStorage.setItem("sidebarState", state.isOpen ? "open" : "closed");
    },

    /**
     * Explicitly sets sidebar state to open.
     * Updates localStorage accordingly.
     */
    openSidebar: (state) => {
      state.isOpen = true;
      localStorage.setItem("sidebarState", "open");
    },

    /**
     * Explicitly sets sidebar state to closed.
     * Updates localStorage accordingly.
     */
    closeSidebar: (state) => {
      state.isOpen = false;
      localStorage.setItem("sidebarState", "closed");
    },
  },
});

// Export individual actions for use throughout the app
export const { toggleSidebar, openSidebar, closeSidebar } = sidebarSlice.actions;

// Export the reducer to be included in the Redux store
export default sidebarSlice.reducer;
